/****************************************************
 * Module Name:  PricePlanning.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { LoadingButton } from "@mui/lab";
import { Box, Checkbox, CircularProgress, ThemeProvider, createTheme, FormControlLabel, FormGroup, Stack, TextField, Tooltip, Typography, Button } from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import React, { useState, useEffect, useMemo, useRef } from "react";
import Constants from "../../../utility/constants";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Utility from "../../../utility/utility";
import NoPermission from "../../common/noPermission";
import PricePlanningProxy from "../../../utility/proxy/manager/manufacturingProxy/pricePlanningProxy";
import TabMenuSnackbar from "../../common/tabMenuSnackbar";
import { BiSearchAlt2 } from "react-icons/bi";
import renderCellExpand from "../../common/dataGridCellExpand";
import { FiRefreshCcw } from "react-icons/fi";
import { IoIosLock } from "react-icons/io";
import EditIcon from '@mui/icons-material/Edit';
import NivodaProxy from "../../../utility/proxy/manager/nivodaProxy";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import KapanHistoryModal from "./kapanHistoryModal";
import PacketHistoryModal from "../../common/packetHistoryModal";
import PricePlanDetailModal from "./pricePlanDetailModal";
import PageTitle from "../../common/pageTitle";

function getNivodaStockByStoneId(stoneid) {
    return new Promise((resolve, reject) => {
        try {
            NivodaProxy.GetNivodaStockByStoneId(stoneid, (responseData) => {
                resolve(responseData);
            });
        } catch (error) {
            console.error('Error fetching products', error);
            reject(error); // Reject the Promise if an error occurs
        }
    });
}

const PricePlanning = ({ triggerSearch, setTriggerSearch }) => {
    const { apiRef } = Utility.useSingleClickEdit();
    const [pricePlanningPermission] = React.useState(Constants.USER_PERMISSION.filter(
        (iPermission) =>
            iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_PRICEPLANNING ||
            iPermission.permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_PRICEPLANNING_MARKER

    ));

    const isChecker = React.useMemo(() => {
        return pricePlanningPermission &&
            pricePlanningPermission[0].permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_PRICEPLANNING_MARKER
            ? false
            : pricePlanningPermission &&
                pricePlanningPermission[0].permissionid === Constants.PERMISSIONS.MANUFACTURING_OPERATION_PRICEPLANNING
                ? true
                : null;
    }, [pricePlanningPermission]);

    const [kapanid, setKapanId] = useState("");
    const [packetid, setPacketId] = useState();
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [autoRefreshStart, setAutoRefreshStart] = useState(false);
    const [outerLoader, setOuterLoader] = useState();
    const [middleLoader, setMiddleLoader] = useState();
    const [lockLoader, setLockLoader] = useState();
    const [refetchLoader, setRefetchLoader] = useState();
    const [deletePlanLoader, setDeletePlanLoader] = useState();
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);

    const [rows, setRows] = useState([]);
    const [rowsDataMap, setRowsDataMap] = useState({});

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [filterValues, setFilterValues] = useState({});
    const [outerSelectionModel, setOuterSelectionModel] = useState([]);
    const [innerSelectionModel, setInnerSelectionModel] = useState(new Map());
    const [discountMap, setDiscountMap] = useState(new Map());

    const packetIdRef = useRef(packetid);
    const kapanIdRef = useRef(kapanid);

    const [kapanIdToView, setKapanIdToView] = useState(null);
    const [packetIdToView, setPacketIdToView] = useState(null);

    const [showPricePlanDetailModal, setShowPricePlanDetailModal] = useState(false);
    const [stoneId, setStoneId] = useState("");
    const [selectedRow, setSelectedRow] = useState({});

    useEffect(() => {
        PageTitle("MANUFACTURING_OPERATION_PRICEPLANNING");
    }, []);

    useEffect(() => {
        if (triggerSearch) {
            handleSearch();
        }
    }, [triggerSearch]);

    useEffect(() => {
        let id = null;

        if (autoRefresh && autoRefreshStart) {
            id = setInterval(() => {
                handleSearch();
            }, 30000);
        }

        return () => {
            if (id) clearInterval(id);
        };
    }, [autoRefresh, autoRefreshStart]);

    const handleSearch = () => {
        setAutoRefreshStart(true);
        setOuterLoader(true);
        let data = {
            userid: Constants.loggedInUser.id,
            kapanid: kapanIdRef.current,
            packetid: packetIdRef.current,
            status: "IN_PROGRESS",
            date: null,
            ischecker: isChecker,
        };

        PricePlanningProxy.getAllPricePlanningByFilters(data, (responseData) => {
            if (responseData) {
                // Generate unique filter values
                const transformedData = responseData.map(item => ({
                    ...item,
                    priceplanningdetail: item.priceplanningdetail.map(detail => ({
                        ...detail,
                        stonetype: detail.stonetype === "L" ? "Lab Grown Diamonds" : "Natural Diamonds",
                        rapnetprice: detail.currentrapnetprice,
                    })),
                }));
                const response = transformedData.flatMap(item => item.priceplanningdetail);

                const uniqueFilterValues = {
                    createdbyusername: Utility.getDistinctOptions(response, "createdbyusername"),
                    shape: Utility.getDistinctOptions(response, "shape"),
                    color: Utility.getDistinctOptions(response, "color"),
                    cut: Utility.getDistinctOptions(response, "cut"),
                    pol: Utility.getDistinctOptions(response, "pol"),
                    sym: Utility.getDistinctOptions(response, "sym"),
                    clarity: Utility.getDistinctOptions(response, "clarity"),
                    flo: Utility.getDistinctOptions(response, "flo"),
                    stonetype: Utility.getDistinctOptions(response, "stonetype"),
                    lockedbyusername: Utility.getDistinctOptions(response, "lockedbyusername"),
                    status: Utility.getDistinctOptions(response, "status"),
                    lab: Utility.getDistinctOptions(response, "lab"),
                    colortype: Utility.getDistinctOptions(response, "colortype"),
                    fancycolor1: Utility.getDistinctOptions(response, "fancycolor1"),
                    fancycolor2: Utility.getDistinctOptions(response, "fancycolor2"),
                    fromintensity: Utility.getDistinctOptions(response, "fromintensity"),
                    tointensity: Utility.getDistinctOptions(response, "tointensity"),
                    overtone: Utility.getDistinctOptions(response, "overtone"),
                };

                // Map response data
                let tempResponseData = [];
                try {
                    tempResponseData = transformedData.map(iResponse => {
                        if (iResponse.priceplanningdetail && iResponse.priceplanningdetail.length > 0) {
                            iResponse.priceplanningdetail.forEach(iData => {
                                if (iData.nivodastonepricing && iData.nivodastonepricing.priceList) {
                                    iData.nivodastonepricing.priceList.forEach((iPriceList, index) => {
                                        iData[`${index + 1}`] =
                                            iPriceList.discount + ' | ' + iPriceList.price + ' (' + iPriceList.noOfStones + ')';
                                    });
                                }
                            })
                        }
                        return iResponse;
                    });
                } catch (error) {
                    console.error("Error processing response data:", error);
                }

                // Set states
                setFilterValues(uniqueFilterValues);
                setOuterLoader(false);
                setRows(tempResponseData);

                let dataMap = new Map();
                let priceMap = new Map();
                if (responseData.length !== 0) {
                    responseData.forEach(iData => {
                        let ids = []
                        iData.priceplanningdetail && iData.priceplanningdetail.forEach(iRecord => {
                            ids.push(iRecord.id);
                            priceMap.set(iRecord.id, iRecord.nivodastonepricing && iRecord.nivodastonepricing.priceList != null && iRecord.nivodastonepricing.priceList.length > 0);
                        })
                        dataMap.set(iData.embededkey, ids);
                    })
                    setRowsDataMap(dataMap);
                }
            } else {
                setOuterLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    }

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];
        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.id,
                    field: field,
                    value: newRow[field]
                });
            }
        });

        return changedFields;
    };

    const processRowUpdate = (newR, oldRow, rowsx, discountMap) => {
        const changedFeilds = getChangedFields(newR, oldRow);

        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];
            let newRow = { ...oldRow };

            const updatedRow = rowsx.map(iRow => {
                const updatedPricePlanningDetail = iRow.priceplanningdetail.map(row => {
                    if (row.id === id) {
                        if (field === "discount" && parseFloat(value) !== parseFloat(row.discount)) {
                            const discount = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));

                            if (row.currentrapnetprice !== null) {
                                const rapnetPrice = parseFloat(row.currentrapnetprice);

                                const ppc = (rapnetPrice + (rapnetPrice * (discount / 100))).toFixed(2);
                                const stoneprice = (parseFloat(ppc) * parseFloat(row.size)).toFixed(2);
                                let total = stoneprice;

                                if (row.iscertified && row.labprice != null) {
                                    total = (parseFloat(total) - parseFloat(row.labprice)).toFixed(2);
                                }
                                const newRow = {
                                    ...row,
                                    [field]: discount,
                                    ppc: ppc,
                                    stoneprice: stoneprice,
                                    total: total,
                                };

                                let discMap = new Map(discountMap);
                                discMap.set(row.id, parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS)));
                                setDiscountMap(discMap);

                                return newRow;
                            } else {
                                setShowSnackbar(true);
                                setSnackbarMessage("Rapnet Data not found for id: [" + row.id + "]");
                                setSnackbarVariant("warning");

                                const newRow = {
                                    ...row,
                                    [field]: discount,
                                };

                                let discMap = new Map(discountMap);
                                discMap.set(row.id, parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS)));
                                setDiscountMap(discMap);
                                return newRow;
                            }
                        }
                    }
                    return row;
                });

                return {
                    ...iRow,
                    priceplanningdetail: updatedPricePlanningDetail,
                };
            });

            setRows(updatedRow);
            return newRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newR };
        }
    }

    const uniqueids = () => {
        let ids = [];
        outerSelectionModel.forEach((embededkey) => {
            ids = ids.concat(rowsDataMap.get(embededkey));
        });
        if (Object.keys(innerSelectionModel).length > 0) {
            ids = ids.concat(Object.values(innerSelectionModel).flat().join(",").split(",").map(Number));
        }
        return Array.from(new Set(ids));
    }

    const deletePlan = () => {
        setDeletePlanLoader(true);
        let ids = uniqueids();

        if (ids.length > 0) {
            PricePlanningProxy.deletePlansById(ids, (response) => {
                if (response) {
                    setShowSnackbar(true);
                    if (response.status === "Success") {
                        setSnackbarMessage("Selected Plans Deleted Successfully");
                        setSnackbarVariant("success");
                        handleSearch();
                    }
                } else {
                    setShowSnackbar(true);
                    setSnackbarMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                }
            })
        }
        setDeletePlanLoader(false);
    }

    const lockDiscount = () => {
        setLockLoader(true);
        let data = [];
        let ids = uniqueids();

        if (ids.length > 0) {
            ids.forEach(id => {
                data.push({
                    id: id,
                    discount: discountMap.get(id) || 0,
                })
            });
        }

        if (data.length > 0) {
            PricePlanningProxy.lockDiscount(Constants.loggedInUser.id, data, (response) => {
                if (response) {
                    setShowSnackbar(true);
                    if (response.status === "Success") {
                        setSnackbarMessage("Discount Locked Successfully");
                        setSnackbarVariant("success");
                        handleSearch();
                    } else if (response.status === "Warning") {
                        setSnackbarMessage(response.errorMessage);
                        setSnackbarVariant("warning");
                    }
                } else {
                    setShowSnackbar(true);
                    setSnackbarMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                }
            })
        }
        setLockLoader(false);
    }

    const refetch = () => {
        setRefetchLoader(true);

        let data = [];
        let ids = uniqueids();
        if (ids.length > 0) {
            ids.forEach(id => {
                data.push({
                    stoneid: id.toString(),
                    filtertype: "Similar or Better",
                    priority: 0,
                })
            });
        }
        if (data.length > 0) {
            PricePlanningProxy.RefetchPricePlanningCriteria(data, (responseData) => {
                if (responseData) {
                    setShowSnackbar(true);
                    setSnackbarMessage("Criteria will be refetched soon !!");
                    setSnackbarVariant("success");
                    handleSearch();
                } else {
                    setShowSnackbar(true);
                    setSnackbarMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                }

            })
        }
        setRefetchLoader(false);
    }

    const handleCellClick = (params, rowsx, discountMap) => {

        const { colDef, id, field } = params;
        if (colDef.editable)
            apiRef.current.startCellEditMode({ id, field });

        const allowedColumns = Array.from({ length: 11 }, (_, index) => `${index + 1}`);

        if (allowedColumns.includes(params.field) && params.value !== undefined && params.value !== null && params.value !== "") {
            let tempRows = rowsx.map(iRow => {
                iRow.priceplanningdetail.forEach(iData => {
                    if (iData.id === params.id) {
                        iData.nivodastonepricing && iData.nivodastonepricing.priceList.forEach(iPriceList => {
                            if (iPriceList.position === Number(params.field)) {
                                iData.discount = iPriceList.discount;

                                if (iData.currentrapnetprice !== null) {
                                    if (iData.discount != null) {
                                        const rapnetPrice = parseFloat(iData.currentrapnetprice);
                                        const discount = parseFloat(iData.discount);
                                        const ppc = (rapnetPrice + (rapnetPrice * (discount / 100))).toFixed(2);
                                        const stoneprice = (parseFloat(ppc) * parseFloat(iData.size)).toFixed(2);
                                        let total = stoneprice;

                                        if (iData.iscertified && iData.labprice != null) {
                                            total = (parseFloat(total) - parseFloat(iData.labprice)).toFixed(2);
                                        }

                                        iData.ppc = ppc;
                                        iData.stoneprice = stoneprice;
                                        iData.total = total;

                                        let discMap = new Map(discountMap);
                                        discMap.set(iData.id, iPriceList.discount);
                                        setDiscountMap(discMap);
                                    }
                                    else {
                                        setShowSnackbar(true);
                                        setSnackbarMessage("Discount is null");
                                        setSnackbarVariant("warning");
                                    }
                                } else {
                                    setShowSnackbar(true);
                                    setSnackbarMessage("Rapnet Data not found for id: [" + iData.id + "]");
                                    setSnackbarVariant("warning");
                                }

                            }
                        })
                    }
                })
                return iRow;
            })
            setRows(tempRows);
        }
    }

    const handleViewButtonClick = (selectedRow) => {
        setSelectedRow(selectedRow)
        setStoneId(selectedRow.id);
        setShowPricePlanDetailModal(true);
    }

    const getInnerColumns = (innerFilterValues) => [
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'shape',
            headerName: 'SHAPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'carat',
            headerName: 'SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'color',
            headerName: 'COLOR',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 65
        },
        {
            field: 'clarity',
            headerName: 'CLARITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 75
        },
        {
            field: 'cut',
            headerName: 'CUT',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'pol',
            headerName: 'POL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'sym',
            headerName: 'SYM',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'flo',
            headerName: 'FLO',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'stonetype',
            headerName: 'STONE TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueGetter: (params) => params.value === "L" ? "Lab Grown Diamonds" : "Natural Diamonds",
            valueOptions: innerFilterValues ? innerFilterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 150
        },
        {
            field: 'discount',
            headerName: 'DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 80,
            valueFormatter: (params) => params.value,
        },
        {
            field: "priceperct",
            headerName: "PPC",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 60,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "diamondprice",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 110,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "location",
            headerName: "LOC",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.location : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "position",
            headerName: "POSITION",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 100,
            renderCell: renderCellExpand
        },
    ];

    const outerColumns = [
        {
            field: 'kapanid',
            headerName: 'KAPAN ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => {
                        setKapanIdToView(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
            ),
            width: 180
        },
        {
            field: 'packetid',
            headerName: 'PACKET ID',
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => {
                        setPacketIdToView(params.value)
                        setKapanIdToView(params.row.kapanid);
                        setShowPacketDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
            ),
            width: 180
        },
        {
            field: 'planid',
            headerName: 'PLAN ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalcount',
            headerName: 'COUNT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalsize',
            headerName: 'TOTAL SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
    ];

    const middleColumns = useMemo(() => [
        {
            field: 'view',
            headerName: 'VIEW',
            headerAlign: 'center',
            align: 'center',
            width: 100,
            renderCell: (params) => (
                <button
                    style={{
                        padding: '5px 10px',
                        cursor: 'pointer',
                        backgroundColor: '#000000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                    }}
                    onClick={() => handleViewButtonClick(params.row)}>
                    VIEW
                </button>
            ),
        },
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'shape',
            headerName: 'SHAPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'size',
            headerName: 'SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'colortype',
            headerName: 'COLOR TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.colortype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 95
        },
        {
            field: 'color',
            headerName: 'COLOR',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 65
        },
        {
            field: 'fancycolor1',
            headerName: 'FANCY COLOR 1',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.fancycolor1 : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 115
        },
        {
            field: 'fancycolor2',
            headerName: 'FANCY COLOR 2',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.fancycolor2 : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 115
        },
        {
            field: 'fromintensity',
            headerName: 'FROM INTENSITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.fromintensity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 125
        },
        {
            field: 'tointensity',
            headerName: 'TO INTENSITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.tointensity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'overtone',
            headerName: 'OVERTONE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.overtone : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 90
        },
        {
            field: 'clarity',
            headerName: 'CLARITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 75
        },
        {
            field: 'cut',
            headerName: 'CUT',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'pol',
            headerName: 'POL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'sym',
            headerName: 'SYM',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'flo',
            headerName: 'FLO',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'lab',
            headerName: 'LAB',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lab : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'stonetype',
            headerName: 'STONE TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueGetter: (params) => params.value,
            valueOptions: filterValues ? filterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value,
            width: 200
        },
        {
            field: 'discount',
            headerName: 'DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 80,
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : '0.00'}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell'
        },
        {
            field: 'rapnetprice',
            headerName: 'RAP',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'ppc',
            headerName: 'PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'labprice',
            headerName: 'LAB PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'total',
            headerName: 'TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'stoneprice',
            headerName: 'STONE PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 120
        },
        ...Array.from({ length: 11 }, (_, index) => ({
            field: `${index + 1}`,
            headerName: `${index + 1}`,
            headerAlign: "center",
            align: "center",
            width: 110,
            renderCell: (params) => {
                return params.value
            },
        })),
        {
            field: 'createdby',
            headerName: 'CREATED BY',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'createdbyusername',
            headerName: 'CREATED BY USERNAME',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.createdbyusername : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 150
        },
        {
            field: 'createdat',
            headerName: 'CREATED AT',
            headerAlign: 'center',
            align: 'center',
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            },
            width: 150
        },
        {
            field: 'updatedat',
            headerName: 'UPDATED AT',
            headerAlign: 'center',
            align: 'center',
            width: 150,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: 'status',
            headerName: 'STATUS',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'iscertified',
            headerName: 'IS CERTIFIED',
            headerAlign: 'center',
            align: 'center',
            type: "boolean",
            width: 100
        },

    ], [filterValues]);

    const getDetailPanelContentInnerTableHeight = React.useCallback(() => 510, []);

    const getDetailPanelContentInnerTable = React.useCallback((row) => <DetailPanelContentInnerTable row={row} />,
        [],);

    function DetailPanelContentInnerTable({ row: rowProp }) {
        const [innerFilterValues, setInnerFilterValues] = useState({});
        const [innerLoader, setInnerLoader] = useState(true);
        let innerColumns = getInnerColumns(innerFilterValues);
        const [innerRowDetails, setInnerRowsDetails] = React.useState([]);
        let response;

        React.useEffect(() => {
            let isMounted = true;

            (async () => {
                response = await getNivodaStockByStoneId(rowProp.id);
                if (response) {
                    const uniqueFilterValues = {
                        shape: Utility.getDistinctOptions(response, "shape"),
                        color: Utility.getDistinctOptions(response, "color"),
                        cut: Utility.getDistinctOptions(response, "cut"),
                        pol: Utility.getDistinctOptions(response, "pol"),
                        sym: Utility.getDistinctOptions(response, "sym"),
                        clarity: Utility.getDistinctOptions(response, "clarity"),
                        flo: Utility.getDistinctOptions(response, "flo"),
                        stonetype: Utility.getDistinctOptions(response, "stonetype"),
                        status: Utility.getDistinctOptions(response, "status"),
                        location: Utility.getDistinctOptions(response, "location"),
                    };

                    setInnerFilterValues(uniqueFilterValues);
                    let position = 1;
                    let responseData = response
                        .map(iResponse => ({ ...iResponse, diamondprice: Math.round(iResponse.diamondprice) }))
                        .sort((a, b) => a.diamondprice - b.diamondprice)
                        .map((iData, index, array) => {
                            if (index === 0 || iData.diamondprice === array[index - 1].diamondprice) {
                                iData.position = position;
                            } else {
                                iData.position = ++position;
                            }
                            return iData;
                        });
                    setInnerRowsDetails(responseData);
                }
                if (isMounted) {
                    setInnerLoader(false);
                }
            })();

            return () => {
                isMounted = false;
            };
        }, [rowProp.id]);

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "96vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: '45vh',
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={innerColumns}
                        rows={innerRowDetails}
                        getRowId={(row) => row.id}
                        loading={innerLoader}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            height: "45vh"
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    id: false,
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }


    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback((row, rowsx, discountMap, middleColumns1, innerSelectionModel1) => <DetailPanelContent row={row} rowsx={rowsx} discountMap={discountMap} middleColumns1={middleColumns1} innerSelectionModel1={innerSelectionModel1} />,
        [],);

    function DetailPanelContent({ row: rowProp, rowsx, discountMap, middleColumns1, innerSelectionModel1 }) {

        setMiddleLoader(true);
        let middlerows = rowProp.priceplanningdetail;
        setMiddleLoader(false);
        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "90vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: 'auto',
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={middleColumns1}
                        rows={middlerows}
                        getRowId={(row) => row.id}
                        checkboxSelection={true}
                        onRowSelectionModelChange={(newSelectionModel) => {
                            setInnerSelectionModel((prevSelection) => ({
                                ...prevSelection,
                                [rowProp.embededkey]: newSelectionModel,
                            }));
                        }}
                        loading={middleLoader}
                        processRowUpdate={(newR, oldRow) => processRowUpdate(newR, oldRow, rowsx, discountMap)}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        rowSelectionModel={innerSelectionModel1[rowProp.embededkey]}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: '50vh',
                        }}
                        // loading={isLoading}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    id: false,
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        onCellClick={(params) => handleCellClick(params, rowsx, discountMap)}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        getDetailPanelHeight={getDetailPanelContentInnerTableHeight}
                        getDetailPanelContent={({ row }) =>
                            getDetailPanelContentInnerTable(row)
                        }
                        apiRef={apiRef}
                        editMode="cell"
                    />
                </Box>
            </Stack>
        );
    }

    const defaultMaterialTheme = createTheme();

    return (
        Utility.isReadAllowed(pricePlanningPermission) ? (
            <div>

                <TabMenuSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        (snackbarVariant === "error" ? "Failed" : "Warning")}
                    handleClose={() => setShowSnackbar(false)}
                />

                {showKapanDetailModal ? (
                    <KapanHistoryModal
                        show={showKapanDetailModal}
                        kapanId={kapanIdToView}
                        close={() => setShowKapanDetailModal(false)}
                    />
                ) : null}

                {showPacketDetailModal ? (
                    <PacketHistoryModal
                        show={showPacketDetailModal}
                        packetId={packetIdToView}
                        kapanId={kapanIdToView}
                        close={() => setShowPacketDetailModal(false)}
                    />
                ) : null}

                {showPricePlanDetailModal ? (
                    <PricePlanDetailModal
                        show={showPricePlanDetailModal}
                        stoneId={stoneId}
                        kapanid={selectedRow.kapanid}
                        packetid={selectedRow.packetid}
                        handleClose={() => setShowPricePlanDetailModal(false)}
                    />
                ) : null}
                <Box sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "30px",
                    width: "60%",
                    paddingTop: "13px",
                    "& .MuiOutlinedInput-input": {
                        padding: "9px !important",
                        height: "25px !important"
                    },
                    "& .MuiInputLabel-root": {
                        top: "-7px !important"
                    },
                    "& .MuiOutlinedInput-root": {
                        padding: 0
                    }
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <Typography sx={{ width: "20%", mr: 2 }}>Kapan Id</Typography>
                        <TextField
                            sx={{ width: "80%", margin: 0 }}
                            margin="normal"
                            label="Kapan Id"
                            placeholder="Enter Kapan Id"
                            value={kapanid}
                            onChange={(e) => {
                                if (/^[1-9]\d*$/.test(e.target.value) || e.target.value === "") {
                                    kapanIdRef.current = e.target.value;
                                    setKapanId(e.target.value);
                                }
                            }}
                        />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <Typography sx={{ width: "20%", mr: 2 }}>Packet Id</Typography>
                        <TextField
                            sx={{ width: "80%", margin: 0 }}
                            margin="normal"
                            label="Packet Id"
                            placeholder="Enter Packet Id"
                            multiline
                            value={packetid}
                            onChange={(e) => {
                                let packetid = Utility.ConvertNewLineToComma(e.target.value);
                                packetIdRef.current = packetid;
                                setPacketId(packetid);
                            }}

                        />
                    </Box>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        "& .MuiFormControlLabel-root": {
                            margin: 0,
                        },
                        "& .MuiFormGroup-root": {
                            width: "30%"
                        }
                    }}>
                        <Typography sx={{ width: "70%" }}>Auto Refresh</Typography>
                        <FormGroup>
                            <FormControlLabel control={
                                <Checkbox
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    size="small"
                                    checked={autoRefresh}
                                />
                            } />
                        </FormGroup>
                    </Box>
                    <Box sx={{
                        "& .Mui-disabled": {
                            backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                            color: "rgba(0, 0, 0, 0.26) !important"
                        }
                    }}>
                        <LoadingButton
                            sx={{ mr: 1 }}
                            variant="contained"
                            color="info"
                            onClick={() => handleSearch()}
                            loading={outerLoader}
                            loadingPosition="start"
                            startIcon={<BiSearchAlt2 />}
                        >
                            Search
                        </LoadingButton>
                    </Box>
                </Box>
                <ThemeProvider theme={defaultMaterialTheme}>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            paddingTop: "20px",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .lavenderCell": {
                                color: "#080808",
                                backgroundColor: "#D8BFD8",
                            },
                            height: "71vh",
                        }}
                    >
                        <DataGridPro
                            rowHeight={65}
                            disableRowSelectionOnClick={true}
                            density="compact"
                            columns={outerColumns}
                            rows={rows}
                            loading={outerLoader}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                            getRowId={(row) => row.embededkey}
                            checkboxSelection={true}
                            onRowSelectionModelChange={(newSelectionModel) => {
                                setOuterSelectionModel(newSelectionModel);
                            }}
                            rowSelectionModel={outerSelectionModel}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        id: false,
                                        createdby: false,
                                        createdbyusername: false,
                                        lockedby: false,
                                        status: false,
                                        rapnetdate: false,
                                        updatedat: false,
                                        createdat: false
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            getDetailPanelHeight={getDetailPanelHeight}
                            getDetailPanelContent={({ row }) => getDetailPanelContent(row, rows, discountMap, middleColumns, innerSelectionModel)}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                top: "27px",
                                right: "265px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                "& .MuiButtonBase-root": {
                                    minHeight: "30px",
                                },
                                "& .Mui-disabled": {
                                    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                    color: "rgba(0, 0, 0, 0.26) !important"
                                }
                            }}
                        >
                            {outerLoader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => handleSearch()}
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer", marginRight: "5px" }}
                                    />
                                </Tooltip>
                            )}
                            <LoadingButton
                                variant="outlined"
                                onClick={() => deletePlan()}
                                loading={deletePlanLoader}
                                disabled={(Object.values(innerSelectionModel).flat().length === 0 && outerSelectionModel.length === 0) || outerLoader || middleLoader}
                                loadingPosition="start"
                                startIcon={<DeleteForeverIcon size={18} />}
                            >
                                DELETE
                            </LoadingButton>
                            <LoadingButton
                                sx={{ m: "0 10px" }}
                                variant="outlined"
                                onClick={() => lockDiscount()}
                                loading={lockLoader}
                                disabled={(Object.values(innerSelectionModel).flat().length === 0 && outerSelectionModel.length === 0) || outerLoader || middleLoader}
                                loadingPosition="start"
                                startIcon={<IoIosLock size={18} />}
                            >
                                LOCK
                            </LoadingButton>
                            <LoadingButton
                                variant="outlined"
                                component="label"
                                sx={{ mr: 1 }}
                                onClick={refetch}
                                loading={refetchLoader}
                                disabled={(Object.values(innerSelectionModel).flat().length === 0 && outerSelectionModel.length === 0) || outerLoader || middleLoader}
                                loadingPosition="start"
                                startIcon={<FiRefreshCcw size={16} />}
                            >
                                RE-FETCH
                            </LoadingButton>
                        </Box>
                    </Box>
                </ThemeProvider>
            </div >
        ) : <NoPermission />
    );
};

export default PricePlanning;